// ============================================================
// Edel CRM — Shared Constants
// ============================================================

// ── Appointment Statuses ────────────────────────────────────
export const APPOINTMENT_STATUSES = [
  { value: "pending", label: "Bekliyor", color: "orange" },
  { value: "confirmed", label: "Onaylandı", color: "blue" },
  { value: "completed", label: "Tamamlandı", color: "green" },
  { value: "cancelled", label: "İptal", color: "red" },
  { value: "expired", label: "Süresi Doldu", color: "default" },
  { value: "blocked", label: "Engellendi", color: "volcano" },
] as const;

export const APPOINTMENT_STATUS_MAP = Object.fromEntries(
  APPOINTMENT_STATUSES.map((s) => [s.value, s])
) as Record<string, (typeof APPOINTMENT_STATUSES)[number]>;

// ── Lead Statuses ───────────────────────────────────────────
export const LEAD_STATUSES = [
  { value: "new", label: "Yeni", color: "#3b82f6" },
  { value: "contacted", label: "İletişime Geçildi", color: "#8b5cf6" },
  { value: "qualified", label: "Nitelikli", color: "#f59e0b" },
  { value: "negotiation", label: "Müzakere", color: "#06b6d4" },
  { value: "won", label: "Kazanıldı", color: "#16a34a" },
  { value: "lost", label: "Kaybedildi", color: "#ef4444" },
] as const;

export const LEAD_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  LEAD_STATUSES.map((s) => [s.value, s.label])
);

export const LEAD_STATUS_COLORS: Record<string, string> = Object.fromEntries(
  LEAD_STATUSES.map((s) => [s.value, s.color])
);

// ── Lead Activities ─────────────────────────────────────────
export const LEAD_ACTIVITY_TYPE_LABELS: Record<string, string> = {
  call: "Arama",
  note: "Not",
  status: "Durum Değişikliği",
  assign: "Atama",
};

export const LEAD_ACTIVITY_TYPE_COLORS: Record<string, string> = {
  call: "blue",
  note: "default",
  status: "purple",
  assign: "cyan",
};

export const CALL_OUTCOME_OPTIONS = [
  { value: "reached", label: "Ulaşıldı" },
  { value: "no_answer", label: "Cevap Yok" },
  { value: "busy", label: "Meşgul" },
  { value: "callback", label: "Geri Aranacak" },
  { value: "wrong_number", label: "Yanlış Numara" },
  { value: "not_interested", label: "İlgilenmiyor" },
];

export const CALL_OUTCOME_LABELS: Record<string, string> = Object.fromEntries(
  CALL_OUTCOME_OPTIONS.map((o) => [o.value, o.label])
);

// ── Leads Pipeline (FAZ B — "Excel sayfaları") ──────────────
// Kod değerleri saklanır; etiket/renk UI'da eşlenir (LEAD_STATUSES paterni).
export interface LeadPipelineOption {
  value: string;
  label: string;
  color: string;
}

export const LEAD_POTENTIAL_OPTIONS: LeadPipelineOption[] = [
  { value: "hot", label: "🔥 Sıcak", color: "red" },
  { value: "warm", label: "⏳ Ilık", color: "orange" },
  { value: "cold", label: "❄️ Soğuk", color: "blue" },
];

export const LEAD_FIRST_ACTION_OPTIONS: LeadPipelineOption[] = [
  { value: "called_reached", label: "Arandı — Ulaşıldı", color: "green" },
  { value: "called_noanswer", label: "Arandı — Ulaşılamadı", color: "orange" },
  { value: "whatsapp", label: "WhatsApp Atıldı", color: "cyan" },
  { value: "form", label: "Form Dolduruldu", color: "blue" },
];

export const LEAD_TRIAL_OPTIONS: LeadPipelineOption[] = [
  { value: "none", label: "Gerek Yok", color: "default" },
  { value: "planned", label: "Planlandı", color: "blue" },
  { value: "attended_pos", label: "Katıldı — Olumlu", color: "green" },
  { value: "attended_neg", label: "Katıldı — Olumsuz", color: "red" },
  { value: "noshow", label: "Katılmadı / İptal", color: "default" },
];

export const LEAD_SALE_OPTIONS: LeadPipelineOption[] = [
  { value: "won", label: "💰 Kapatıldı", color: "green" },
  { value: "pending", label: "📝 Beklemede", color: "orange" },
  { value: "lost", label: "❌ İptal / Vazgeçti", color: "red" },
];

export const LEAD_OBJECTION_OPTIONS: LeadPipelineOption[] = [
  { value: "budget", label: "Bütçe Yetersiz", color: "default" },
  { value: "expensive", label: "Fiyatı Pahalı Buldu", color: "default" },
  { value: "spouse", label: "Eşini İkna Edemedi", color: "default" },
  { value: "timing", label: "Zamanı Uymadı", color: "default" },
  { value: "other_course", label: "Başka Kursa Kaydoldu", color: "default" },
];

/** value → option (label/color) çözümleyici. */
export const leadOpt = (
  opts: LeadPipelineOption[],
  v: string | null | undefined
): LeadPipelineOption | undefined => opts.find((o) => o.value === v);

// ── Lead Sources ────────────────────────────────────────────
export const SOURCE_OPTIONS = [
  { value: "web_landing", label: "Web Sitesi" },
  { value: "instagram", label: "Instagram" },
  { value: "referral", label: "Referans" },
  { value: "phone", label: "Telefon" },
  { value: "manual", label: "Manuel" },
] as const;

export const SOURCE_LABELS: Record<string, string> = {
  ...Object.fromEntries(SOURCE_OPTIONS.map((s) => [s.value, s.label])),
  // Otomatik kaynaklar (elle seçilmez)
  app_signup: "Uygulama kaydı",
};

export const SOURCE_COLORS: Record<string, string> = {
  app_signup: "purple",
  web_landing: "blue",
  instagram: "magenta",
  referral: "green",
  phone: "orange",
  manual: "default",
};

// ── Lead Variants ───────────────────────────────────────────
export const VARIANT_OPTIONS = [
  { value: "demo", label: "Demo Ders" },
  { value: "1-on-1", label: "1:1 Ders" },
  { value: "group", label: "Grup Dersi" },
  { value: "kids_group", label: "Çocuk Grubu" },
  { value: "course", label: "Video Kurs" },
  { value: "coaching", label: "Koçluk" },
  { value: "consulting", label: "Danışmanlık" },
] as const;

export const VARIANT_COLORS: Record<string, string> = {
  demo: "purple",
  "1-on-1": "blue",
  group: "cyan",
  kids_group: "magenta",
  course: "geekblue",
  coaching: "orange",
  consulting: "gold",
};

// ── Order Statuses ──────────────────────────────────────────
export const ORDER_STATUS_LABELS: Record<string, string> = {
  completed: "Tamamlandı",
  needs_review: "İnceleme Bekliyor",
  pending: "Bekliyor",
  failed: "Başarısız",
  partially_refunded: "Kısmen İade",
  refunded: "İade Edildi",
};

// Ödemesi alınmış sayılan sipariş durumları (gelir/satış hesapları bunları kapsar)
export const PAID_ORDER_STATUSES = [
  "completed",
  "partially_refunded",
  "refunded",
];

export const ORDER_STATUS_COLORS: Record<string, string> = {
  completed: "green",
  needs_review: "gold",
  pending: "orange",
  failed: "red",
  partially_refunded: "volcano",
  refunded: "blue",
};

// ── Order Types (ürün türü) ─────────────────────────────────
// Ekosistem kredi→TL/ders-hakkı geçişi: grup + kulüp + kurs TL; 1:1 "özel ders hakkı".
// null order_type = eski kredi paketi (özel ders hakkı) satışı.
export const ORDER_TYPE_LABELS: Record<string, string> = {
  lesson_pack: "Özel Ders Paketi",
  credits: "Özel Ders Paketi",
  cohort: "Grup Dersi",
  group: "Grup Dersi",
  course: "Video Kurs",
  club_month: "Konuşma Kulübü",
  speaking_club: "Konuşma Kulübü",
};

export const ORDER_TYPE_COLORS: Record<string, string> = {
  lesson_pack: "blue",
  credits: "blue",
  cohort: "orange",
  group: "orange",
  course: "geekblue",
  club_month: "green",
  speaking_club: "green",
};

/** Bir siparişin ürün türü etiketi (null = eski kredi/özel ders paketi). */
export const orderTypeLabel = (t: string | null | undefined): string =>
  ORDER_TYPE_LABELS[t || "lesson_pack"] || "Özel Ders Paketi";

// ── Credit Transaction Types ────────────────────────────────
export const CREDIT_TYPE_LABELS: Record<string, string> = {
  purchase: "Satın Alma",
  usage: "Kullanım",
  lesson_deduct: "Ders",
  refund: "İade",
  order_refund: "Sipariş İadesi",
  cancel_refund: "İptal İadesi",
  bonus: "Bonus",
  speaking_club: "Konuşma Kulübü",
  lesson_payment: "Ders Ödemesi",
  course_purchase: "Kurs Satın Alma",
  admin_add: "Admin Ekleme",
  admin_override: "Admin Düzeltme",
};

// ── Role Labels & Colors ────────────────────────────────────
export const ROLE_LABELS: Record<string, string> = {
  student: "Öğrenci",
  teacher: "Eğitmen",
  admin: "Admin",
};

export const ROLE_COLORS: Record<string, string> = {
  student: "blue",
  teacher: "green",
  admin: "red",
};

// ── Payout Statuses ─────────────────────────────────────────
export const PAYOUT_STATUSES = [
  { value: "pending", label: "Bekliyor", color: "orange" },
  { value: "approved", label: "Onaylandı", color: "blue" },
  { value: "completed", label: "Tamamlandı", color: "green" },
  { value: "rejected", label: "Reddedildi", color: "red" },
] as const;

export const PAYOUT_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  PAYOUT_STATUSES.map((s) => [s.value, s.label])
);

export const PAYOUT_STATUS_COLORS: Record<string, string> = Object.fromEntries(
  PAYOUT_STATUSES.map((s) => [s.value, s.color])
);

// ── Group Session Statuses ──────────────────────────────────
export const GROUP_SESSION_STATUSES = [
  { value: "scheduled", label: "Planlandı", color: "blue" },
  { value: "live", label: "Canlı", color: "green" },
  { value: "completed", label: "Tamamlandı", color: "default" },
  { value: "cancelled", label: "İptal", color: "red" },
] as const;

export const GROUP_SESSION_STATUS_LABELS: Record<string, string> =
  Object.fromEntries(GROUP_SESSION_STATUSES.map((s) => [s.value, s.label]));

export const GROUP_SESSION_STATUS_COLORS: Record<string, string> =
  Object.fromEntries(GROUP_SESSION_STATUSES.map((s) => [s.value, s.color]));

// ── Notification Types ──────────────────────────────────────
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  appointment: "Randevu",
  credit: "Ders Hakkı",
  system: "Sistem",
  message: "Mesaj",
  review: "Değerlendirme",
};

// ── Course Statuses ─────────────────────────────────────────
export const COURSE_STATUS_LABELS: Record<string, string> = {
  active: "Aktif",
  draft: "Taslak",
  archived: "Arşivlendi",
};

export const COURSE_STATUS_COLORS: Record<string, string> = {
  active: "green",
  draft: "orange",
  archived: "default",
};

// ── Material Types ──────────────────────────────────────────
export const MATERIAL_TYPE_LABELS: Record<string, string> = {
  pdf: "PDF",
  video: "Video",
  audio: "Ses",
  document: "Doküman",
  image: "Görsel",
  worksheet: "Çalışma Kağıdı",
};

// ── Material Categories ─────────────────────────────────────
export const MATERIAL_CATEGORY_LABELS: Record<string, string> = {
  lesson: "Ders Materyali",
  homework: "Ödev",
  exam: "Sınav",
  resource: "Kaynak",
  worksheet: "Çalışma Kağıdı",
};

// ── Materyal seviyeleri / kullanım (DB CHECK ile birebir) ──────
// materials_level_check: A1 | A2 | B1 | B2 | mixed (C1/C2 YOK)
export const MATERIAL_LEVEL_OPTIONS = [
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "B1", label: "B1" },
  { value: "B2", label: "B2" },
  { value: "mixed", label: "Karma" },
] as const;
export const MATERIAL_LEVEL_LABEL: Record<string, string> = { A1: "A1", A2: "A2", B1: "B1", B2: "B2", mixed: "Karma" };
// materials_category_check: lesson | homework | interactive
export const MATERIAL_USE_LABEL: Record<string, string> = {
  lesson: "Ders materyali",
  homework: "Ödev",
  interactive: "İnteraktif",
};
// materials_file_type_check: pdf | audio | image | video | html_interactive
export const MATERIAL_FILE_LABEL: Record<string, string> = {
  pdf: "PDF",
  audio: "Ses",
  video: "Video",
  image: "Görsel",
  html_interactive: "İnteraktif",
};

// ── Level Labels ────────────────────────────────────────────
export const LEVEL_LABELS: Record<string, string> = {
  A1: "A1 - Başlangıç",
  A2: "A2 - Temel",
  B1: "B1 - Orta",
  B2: "B2 - Orta Üstü",
  C1: "C1 - İleri",
  C2: "C2 - Uzman",
};

// ── Oyunlaştırma (Gamification) ─────────────────────────────
// edel-app i18n (tr) ile senkron. Seviye slug → Türkçe ad.
export const GAMIFICATION_LEVEL_LABELS: Record<string, string> = {
  beginner: "Başlangıç",
  bronze: "Bronz",
  silver: "Gümüş",
  gold: "Altın",
  platinum: "Platin",
  diamond: "Elmas",
};

// Rozet id → Türkçe ad (badges tablosu + app i18n ile aynı)
export const GAMIFICATION_BADGE_LABELS: Record<string, string> = {
  first_step: "İlk Adım",
  dedicated: "İstikrarlı",
  master: "Dil Ustası",
  marathon: "Maratoncu",
  centurion: "Yüzler Kulübü",
  critic: "Eleştirmen",
  streak_4: "Alev Alev",
  streak_12: "Durdurulamaz",
  level_silver: "Gümüş Çağ",
  level_gold: "Altın Çağ",
  level_diamond: "Elmas Çağ",
  social: "Sosyal",
  scholar: "Kursiyer",
};

// ── Teacher Transaction Types ───────────────────────────────
export const TEACHER_TXN_TYPE_LABELS: Record<string, string> = {
  lesson_income: "Ders Kazancı",
  group_lesson_income: "Grup Ders Kazancı",
  payout_request: "Ödeme Talebi",
  payout_completed: "Ödeme Tamamlandı",
  payout: "Ödeme",
  payout_rejected: "Ödeme Reddedildi",
  monthly_release: "Aylık Serbest Bırakma",
  bonus: "Bonus",
  refund: "İade",
  adjustment: "Düzeltme",
  deduction: "Kesinti",
};

export const TEACHER_TXN_TYPE_COLORS: Record<string, string> = {
  lesson_income: "green",
  group_lesson_income: "cyan",
  payout_request: "blue",
  payout_completed: "geekblue",
  payout: "geekblue",
  payout_rejected: "volcano",
  monthly_release: "purple",
  bonus: "gold",
  refund: "volcano",
  adjustment: "orange",
  deduction: "red",
};

// ── Teacher Transaction Statuses ────────────────────────────
export const TEACHER_TXN_STATUS_LABELS: Record<string, string> = {
  completed: "Tamamlandı",
  pending: "Bekliyor",
  cancelled: "İptal",
};

export const TEACHER_TXN_STATUS_COLORS: Record<string, string> = {
  completed: "green",
  pending: "orange",
  cancelled: "red",
};

// ── Target Audience ─────────────────────────────────────────
export const TARGET_AUDIENCE_LABELS: Record<string, string> = {
  all: "Herkes",
  students: "Tüm Öğrenciler",
  teachers: "Tüm Eğitmenler",
  group: "Grup Oturumu",
  students_specific: "Belirli Öğrenciler",
};

export const TARGET_AUDIENCE_COLORS: Record<string, string> = {
  all: "red",
  students: "blue",
  teachers: "green",
  group: "cyan",
  students_specific: "purple",
};

export const TARGET_AUDIENCE_DESCRIPTIONS: Record<string, string> = {
  all: "Tüm öğrenci ve eğitmenlere bildirim gönderilir",
  students: "Yalnızca tüm öğrencilere bildirim gönderilir",
  teachers: "Yalnızca tüm eğitmenlere bildirim gönderilir",
  group: "Yalnızca seçili grup oturumuna kayıtlı öğrencilere bildirim gönderilir",
  students_specific: "Yalnızca seçtiğiniz öğrenci(ler)e bildirim gönderilir",
};

// ── Notification Types (with colors) ────────────────────────
export const NOTIFICATION_TYPES = [
  { value: "appointment", label: "Randevu", color: "blue" },
  { value: "credit", label: "Ders Hakkı", color: "green" },
  { value: "system", label: "Sistem", color: "red" },
  { value: "message", label: "Mesaj", color: "purple" },
  { value: "review", label: "Değerlendirme", color: "orange" },
  { value: "info", label: "Bilgi", color: "default" },
] as const;

export const NOTIFICATION_TYPE_COLORS: Record<string, string> =
  Object.fromEntries(NOTIFICATION_TYPES.map((t) => [t.value, t.color]));

// ── Speaking Club Sources ───────────────────────────────────
export const SC_SOURCE_LABELS: Record<string, string> = {
  direct: "Direkt",
  promotion: "Promosyon",
  admin: "Admin",
};

// ── Day of Week Labels ──────────────────────────────────────
export const DAY_OF_WEEK_LABELS: Record<number, string> = {
  0: "Pazar",
  1: "Pazartesi",
  2: "Salı",
  3: "Çarşamba",
  4: "Perşembe",
  5: "Cuma",
  6: "Cumartesi",
};

// ── Discount Types ──────────────────────────────────────────
export const DISCOUNT_TYPES = [
  { value: "percentage", label: "Yüzde (%)", color: "blue" },
  { value: "fixed_amount", label: "Sabit Tutar (₺)", color: "green" },
] as const;

export const DISCOUNT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  DISCOUNT_TYPES.map((t) => [t.value, t.label])
);

export const DISCOUNT_TYPE_COLORS: Record<string, string> = Object.fromEntries(
  DISCOUNT_TYPES.map((t) => [t.value, t.color])
);

// ── Audit Log Actions ───────────────────────────────────────
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  create: "Oluşturma",
  update: "Güncelleme",
  delete: "Silme",
  approve: "Onay",
  reject: "Red",
  export: "Dışa Aktarma",
  login: "Giriş",
  send: "Gönderme",
};

export const AUDIT_ACTION_COLORS: Record<string, string> = {
  create: "green",
  update: "blue",
  delete: "red",
  approve: "cyan",
  reject: "orange",
  export: "purple",
  login: "default",
  send: "geekblue",
};

// ── Audit Resources ─────────────────────────────────────────
export const AUDIT_RESOURCE_LABELS: Record<string, string> = {
  appointments: "Randevular",
  credits: "Ders Hakkı",
  coupons: "Kuponlar",
  finance: "Finans",
  groups: "Grup Dersleri",
  leads: "Müşteri Adayları",
  notifications: "Bildirimler",
  profiles: "Kullanıcılar",
  reviews: "Değerlendirmeler",
  speaking_club: "Speaking Club",
  settings: "Ayarlar",
  reports: "Raporlar",
};

// ── User Roles (for create/edit forms) ──────────────────────
export const USER_ROLE_OPTIONS = [
  { value: "student", label: "Öğrenci" },
  { value: "teacher", label: "Eğitmen" },
  { value: "admin", label: "Admin" },
] as const;

// ── Preferred Language ──────────────────────────────────────
export const LANGUAGE_OPTIONS = [
  { value: "tr", label: "Türkçe" },
  { value: "en", label: "İngilizce" },
  { value: "de", label: "Almanca" },
] as const;

// ── Teacher Expertise / Tags ────────────────────────────────
// Mirrors the mobile app + teacher-applications labels so manually
// created teachers carry the same expertise tags end-to-end.
export const TEACHER_EXPERTISE_OPTIONS = [
  // Seviye
  { value: "a1", label: "A1" },
  { value: "a2", label: "A2" },
  { value: "b1", label: "B1" },
  { value: "b2", label: "B2" },
  { value: "c1", label: "C1" },
  { value: "c2", label: "C2" },
  // Uzmanlık & Odak
  { value: "examPrep", label: "Sınav Hazırlığı" },
  { value: "business", label: "İş Almancası" },
  { value: "academic", label: "Akademik Almanca" },
  { value: "everyday", label: "Günlük & Seyahat" },
  { value: "conversation", label: "Konuşma & Akıcılık" },
  { value: "grammar", label: "Gramer & Yapı" },
  { value: "kids", label: "Çocuklara Almanca" },
] as const;

export const TEACHER_EXPERTISE_LABELS: Record<string, string> =
  Object.fromEntries(TEACHER_EXPERTISE_OPTIONS.map((o) => [o.value, o.label]));

// ── Maliye (Finance / Treasury) ─────────────────────────────
export const FINANCE_ACCOUNT_TYPE_LABELS: Record<string, string> = {
  gateway: "Ödeme Kuruluşu",
  bank: "Banka",
  cash: "Nakit Kasa",
  other: "Diğer",
};
export const FINANCE_ACCOUNT_TYPE_COLORS: Record<string, string> = {
  gateway: "purple",
  bank: "blue",
  cash: "green",
  other: "default",
};

export const FINANCE_DIRECTION_LABELS: Record<string, string> = {
  income: "Gelir",
  expense: "Gider",
  transfer: "Transfer",
};
export const FINANCE_DIRECTION_COLORS: Record<string, string> = {
  income: "green",
  expense: "red",
  transfer: "blue",
};

export const FINANCE_CATEGORY_KIND_LABELS: Record<string, string> = {
  income: "Gelir",
  expense: "Gider",
};

export const PAYABLE_KIND_LABELS: Record<string, string> = {
  receivable: "Alacak",
  payable: "Borç",
};
export const PAYABLE_KIND_COLORS: Record<string, string> = {
  receivable: "green",
  payable: "volcano",
};

export const PAYABLE_STATUS_LABELS: Record<string, string> = {
  open: "Açık",
  partial: "Kısmi Ödendi",
  paid: "Kapandı",
  void: "İptal",
};
export const PAYABLE_STATUS_COLORS: Record<string, string> = {
  open: "orange",
  partial: "blue",
  paid: "green",
  void: "default",
};

export const RECURRING_FREQ_LABELS: Record<string, string> = {
  weekly: "Haftalık",
  monthly: "Aylık",
  yearly: "Yıllık",
};
export const RECURRING_FREQ_OPTIONS = [
  { value: "weekly", label: "Haftalık" },
  { value: "monthly", label: "Aylık" },
  { value: "yearly", label: "Yıllık" },
];

/** Türkiye KDV oranları. */
export const KDV_RATE_OPTIONS = [
  { value: 0, label: "%0 (KDV yok)" },
  { value: 1, label: "%1" },
  { value: 10, label: "%10" },
  { value: 20, label: "%20" },
];

export const PAYMENT_METHOD_OPTIONS = [
  { value: "transfer", label: "Havale / EFT" },
  { value: "card", label: "Kredi Kartı" },
  { value: "cash", label: "Nakit" },
  { value: "iyzico", label: "iyzico" },
  { value: "other", label: "Diğer" },
];
export const PAYMENT_METHOD_LABELS: Record<string, string> = Object.fromEntries(
  PAYMENT_METHOD_OPTIONS.map((o) => [o.value, o.label])
);

// ============================================================
// Çalışma Alanları (Workspaces) — CRM / Planlama / Drive
// ============================================================
export type WorkspaceKey = "crm" | "buyume" | "planlama" | "drive";

export interface WorkspaceDef {
  key: WorkspaceKey;
  label: string;
  description: string;
  /** İçine girildiğinde gidilecek ilk route (launcher kartı + app-switcher). */
  path: string;
  /** Kart arkaplanı için 3 renkli gradient dizisi. */
  gradient: readonly [string, string, string];
}

export const WORKSPACES: WorkspaceDef[] = [
  {
    key: "crm",
    label: "CRM Paneli",
    description: "Öğrenciler, randevular, ders hakkı, finans ve tüm operasyon.",
    path: "/",
    gradient: ["#c2410c", "#ea580c", "#f97316"],
  },
  {
    key: "buyume",
    label: "Büyüme",
    description: "Çok-kanallı gelen kutusu, satış panosu ve reklam — WhatsApp & Instagram.",
    path: "/buyume",
    gradient: ["#5b21b6", "#7c3aed", "#a78bfa"],
  },
  {
    key: "planlama",
    label: "Planlama",
    description: "Görevler, toplantı notları ve ekip takvimi.",
    path: "/gorevler",
    gradient: ["#1e3a5f", "#2563eb", "#60a5fa"],
  },
  {
    key: "drive",
    label: "Dosya Paylaşımı",
    description: "Ekip için ortak dosya alanı — yükle, indir, klasörle, paylaş.",
    path: "/drive",
    gradient: ["#14532d", "#16a34a", "#4ade80"],
  },
];

export const WORKSPACE_MAP: Record<WorkspaceKey, WorkspaceDef> =
  Object.fromEntries(WORKSPACES.map((w) => [w.key, w])) as Record<
    WorkspaceKey,
    WorkspaceDef
  >;

/** Route path → çalışma alanı (app-switcher vurgusu + yönlendirme). */
export const workspaceFromPath = (pathname: string): WorkspaceKey => {
  if (pathname.startsWith("/buyume")) return "buyume";
  if (pathname.startsWith("/planlama") || pathname.startsWith("/gorevler")) return "planlama";
  if (pathname.startsWith("/drive")) return "drive";
  return "crm";
};

/**
 * Üst-alan (top-level resource) adı → çalışma alanı.
 * AppSider ikon rayını aktif çalışma alanına göre filtreler.
 */
export const AREA_WORKSPACE: Record<string, WorkspaceKey> = {
  // CRM (mevcut 8 alan)
  dashboard: "crm",
  crm: "crm",
  egitim: "crm",
  ogrenciler: "crm",
  egitmenler: "crm",
  finans: "crm",
  iletisim: "crm",
  sistem: "crm",
  // Büyüme (çok-kanallı gelen kutusu + satış + reklam)
  buyume: "buyume",
  // Planlama
  planlama: "planlama",
  // Drive
  drive: "drive",
};

// ── RBAC: CRM Departman Erişim Alanları (FAZ D) ─────────────
// profiles.crm_scopes boş = tam yetki; dolu = yalnız o alanlar.
export const CRM_SCOPES = [
  { value: "finance", label: "Finans", color: "green" },
  { value: "sales", label: "Satış & CRM", color: "blue" },
  { value: "marketing", label: "Pazarlama / Reklam", color: "purple" },
  { value: "ops", label: "Operasyon / Eğitim", color: "orange" },
] as const;

export const CRM_SCOPE_LABELS: Record<string, string> = Object.fromEntries(
  CRM_SCOPES.map((s) => [s.value, s.label])
);

/** Her CRM kullanıcısının gördüğü ortak alanlar (scope gerekmez). */
export const COMMON_AREAS = ["dashboard", "planlama", "drive"];

/** Üst-alan → gereken scope'lardan herhangi biri. Listede olmayan +
 *  COMMON olmayan alan (ör. sistem) yalnız tam-yetkili kullanıcıya açıktır. */
export const AREA_SCOPES: Record<string, string[]> = {
  crm: ["sales"],
  egitim: ["ops", "sales"],
  ogrenciler: ["ops", "sales"],
  egitmenler: ["ops"],
  finans: ["finance"],
  iletisim: ["ops", "marketing"],
  buyume: ["marketing"],
};

/** Kullanıcı bu üst-alanı görebilir mi? (boş/null scope = tam yetki) */
export function canSeeArea(
  areaKey: string,
  scopes: string[] | null | undefined
): boolean {
  if (!scopes || scopes.length === 0) return true; // tam yetki
  if (COMMON_AREAS.includes(areaKey)) return true;
  const req = AREA_SCOPES[areaKey];
  if (!req) return false; // sistem vb. → yalnız tam yetki
  return req.some((s) => scopes.includes(s));
}

const AREA_BY_PREFIX: [string, string][] = [
  ["/leads", "crm"], ["/coupons", "crm"],
  ["/appointments", "egitim"], ["/groups", "egitim"], ["/cohorts", "egitim"],
  ["/speaking-club", "egitim"], ["/demo", "egitim"], ["/courses", "egitim"],
  ["/materials", "egitim"], ["/kutuphane", "egitim"], ["/homework", "egitim"], ["/odev", "egitim"], ["/geri-bildirim", "egitim"], ["/anket", "egitim"],
  ["/ogrenciler", "ogrenciler"], ["/credits", "ogrenciler"],
  ["/placements", "ogrenciler"], ["/reviews", "ogrenciler"],
  ["/egitmenler", "egitmenler"], ["/teacher-applications", "egitmenler"], ["/availability", "egitmenler"],
  ["/maliye", "finans"], ["/finans", "finans"], ["/finance", "finans"], ["/reports", "finans"],
  ["/announcements", "iletisim"], ["/notifications", "iletisim"], ["/messages", "iletisim"], ["/campaigns", "iletisim"],
  ["/profiles", "sistem"], ["/deleted-accounts", "sistem"], ["/audit", "sistem"], ["/settings", "sistem"], ["/notification-settings", "sistem"], ["/ayarlar", "sistem"],
  ["/planlama", "planlama"], ["/gorevler", "planlama"],
  ["/drive", "drive"],
  ["/buyume", "buyume"],
];

/** Route yolundan üst-alanı çöz (route-guard için). */
export function areaForPath(pathname: string): string {
  if (pathname === "/") return "dashboard";
  const hit = AREA_BY_PREFIX.find(
    ([p]) => pathname === p || pathname.startsWith(p + "/")
  );
  return hit ? hit[1] : "dashboard";
}

// ── Planlama: Görev Durumları (Kanban kolonları) ────────────
export const TASK_STATUSES = [
  { value: "todo", label: "Yapılacak", color: "#64748b" },
  { value: "in_progress", label: "Devam Ediyor", color: "#2563eb" },
  { value: "review", label: "İncelemede", color: "#f59e0b" },
  { value: "done", label: "Tamamlandı", color: "#16a34a" },
] as const;

export const TASK_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  TASK_STATUSES.map((s) => [s.value, s.label])
);
export const TASK_STATUS_COLORS: Record<string, string> = Object.fromEntries(
  TASK_STATUSES.map((s) => [s.value, s.color])
);

// ── Planlama: Görev Öncelikleri ─────────────────────────────
export const TASK_PRIORITIES = [
  { value: "low", label: "Düşük", color: "default" },
  { value: "normal", label: "Normal", color: "blue" },
  { value: "high", label: "Yüksek", color: "orange" },
  { value: "urgent", label: "Acil", color: "red" },
] as const;

export const TASK_PRIORITY_LABELS: Record<string, string> = Object.fromEntries(
  TASK_PRIORITIES.map((p) => [p.value, p.label])
);
export const TASK_PRIORITY_COLORS: Record<string, string> = Object.fromEntries(
  TASK_PRIORITIES.map((p) => [p.value, p.color])
);

// ── Takvim etkinlik renk seçenekleri ────────────────────────
export const EVENT_COLORS = [
  "#ea580c",
  "#2563eb",
  "#16a34a",
  "#9333ea",
  "#dc2626",
  "#0891b2",
] as const;

// ============================================================
// Dosya Paylaşımı (Drive)
// ============================================================
export const DRIVE_BUCKET = "team-drive";
export const DRIVE_MAX_FILE_MB = 50;

/** Güvenlik: yüklenmesi engellenen çalıştırılabilir uzantılar. */
export const DRIVE_BLOCKED_EXTENSIONS = [
  "exe", "msi", "bat", "cmd", "com", "scr", "pif", "cpl", "jar",
  "vbs", "ps1", "sh", "app", "dmg", "deb", "apk",
];

export const DRIVE_ACTIVITY_LABELS: Record<string, string> = {
  upload: "yükledi",
  download: "indirdi",
  rename: "yeniden adlandırdı",
  move: "taşıdı",
  delete: "sildi",
  create_folder: "klasör oluşturdu",
  delete_folder: "klasör sildi",
};

export const DRIVE_ACTIVITY_COLORS: Record<string, string> = {
  upload: "green",
  download: "blue",
  rename: "gold",
  move: "purple",
  delete: "red",
  create_folder: "cyan",
  delete_folder: "volcano",
};
